import Class from '../Class';
import Container from '../Container';
import EventSource from '../EventSource';

import "./AutoCompleteElement.css";

const win = window;
const doc = document;

const createElement = function(tag, className, parent) {
    const element = doc.createElement(tag);
    element.className = className;
    if (parent) {
        parent.appendChild(element);
    }
    return element;
};

const normalize = (function() {
    const normalizations = {
        "a": "\xe0\xe1\xe2\xe3\xe4\xe5",
        "e": "\xe8\xe9\xea\xeb",
        "o": "\xf2\xf3\xf4\xf5\xf6\xf8",
        "u": "\xf9\xfa\xfb\xfc"
    };

    const inverted = new Container();
    Container.forEach(normalizations, function(value, key) {
        for (let i = 0, len = value.length; i < len; i++) {
            inverted.set(value[i], key);
        }
    });

    return function(_string) {
        const string = _string.trim().split(/\s+/).join(" ");

        return Array.prototype.slice.call(string.toLowerCase()).map(function(ch) {
            return inverted.get(ch, ch);
        }).join("");
    };
}());

export default new Class({
    Implements: EventSource,

    initialize: function(element) {
        this._element = element || createElement("div", "autocomplete");
        this._input = createElement("input", "input", this._element);
        this._suggestionsContainer = createElement("div", "suggestions-container", this._element);
        this._suggestions = createElement("div", "suggestions", this._suggestionsContainer);

        this._input.addEventListener("input", this._onInput.bind(this), false);
        this._input.addEventListener("focus", this._onFocus.bind(this), false);
        this._input.addEventListener("blur", this._onBlur.bind(this), false);
        this._input.addEventListener("keydown", this._onKeyDown.bind(this), false);

        this._suggestions.addEventListener("mousedown", this._onMouseDown.bind(this), false);
        this._suggestions.addEventListener("mouseup", this._onMouseUp.bind(this), false);

        this._data = null;

        this._current = [];
        this._inFocus = false;

        this._search = this._input.value;
        this._fallback = this._input.value;

        this._resizeListener = null;
    },

    _onScroll: function(event) {
        if (event.target !== this._suggestions) {
            const inputRect = this._input.getBoundingClientRect();
            const parentRect = event.target.getBoundingClientRect();

            const parentStyles = window.getComputedStyle(event.target);
            const topOffset = parseFloat(parentStyles.borderTopWidth) + parseFloat(parentStyles.paddingTop);

            if (inputRect.bottom < (parentRect.top + topOffset) || inputRect.top > parentRect.bottom) {
                this._suggestionsContainer.style.display = "none";
            } else {
                this._suggestionsContainer.style.display = "";
            }
        }

        this._onResize();
    },

    _onResize: function() {
        const rect = this._input.getBoundingClientRect();
        this._suggestionsContainer.style.width = rect.width + "px";

        const rect2 = this._suggestionsContainer.getBoundingClientRect();
        const offset = rect.bottom + rect2.height + 10 < window.innerHeight ? 0 : rect.height + rect2.height;
        this._suggestionsContainer.style.top = (rect.bottom - offset) + "px";
    },

    _onInput: function() {
        this._search = this._input.value;
        this.trigger("changed");
        this._update();
    },

    _onKeyDown: function(event) {
        if (event.which === 38) {
            // Up arrow
            if (this._search === null) {
                this._search = this._input.value;
                this._update();
            }
            this._select(true);
        } else if (event.which === 40) {
            // Down arrow
            if (this._search === null) {
                this._search = this._input.value;
                this._update();
            }
            this._select(false);
        } else if (event.which === 13) {
            // Enter
            if (this._current.length === 0) {
                this._input.blur();
            } else {
                this._search = null;
                this.set(this._input.value);
            }
        } else if (event.which === 27) {
            // ESC
            if (this._current.length > 0 || this._input.value !== this._fallback) {
                this._search = null;
                this.set(this._fallback);
                event.stopPropagation();
            } else {
                this._input.blur();
            }
        } else {
            return;
        }

        event.preventDefault();
    },

    _select: function(up) {
        if (this._current.length === 0) {
            return;
        }

        let index = 0;
        const selected = this._input.value;

        let currentIndex = up ? 0 : this._current.length - 1;
        for (let i = 0, len = this._current.length; i < len; i++) {
            if (selected === this._current[i].key) {
                currentIndex = i;
                break;
            }
        }

        if (currentIndex !== null) {
            this._current[currentIndex].element.classList.remove("selected");
        }
        index = (currentIndex + this._current.length + (up ? -1 : 1)) % this._current.length;

        this._current[index].element.classList.add("selected");
        this._current[index].element.scrollIntoView(false);
        this._input.value = this._current[index].key;
        this.trigger("changed");
    },

    _onMouseDown: function(event) {
        event.preventDefault();
    },

    _onMouseUp: function(event) {
        event.preventDefault();

        let target = event.target;
        while (target && target.parentNode !== this._suggestions) {
            target = target.parentNode;
        }
        if (!target) {
            return;
        }

        this._search = null;
        this.set(target.dataset.key);
    },

    _onFocus: function() {
        this._search = this._input.value;
        this._fallback = this._input.value;
        this._inFocus = true;
        this._update();
    },

    _onBlur: function() {
        this._inFocus = false;
        this._update();
        this.set(this._input.value);
    },

    _createItem: function(key) {
        const element = createElement("div", "suggestion", this._suggestions);
        element.textContent = key;
        element.dataset.key = key;

        return {
            "key": key,
            "element": element
        };
    },

    _update: function() {
        this._current.forEach(function(item) {
            this._suggestions.removeChild(item.element);
        }, this);
        this._current = [];

        const matches = [];
        let selected = null;

        if (this._data && this._inFocus && this._search !== null) {
            const matcher = this.createMatcher(this._search);

            this._data.forEach(function(_, key) {
                if (matcher(key)) {
                    matches.push(key);
                }
            }, this);


            matches.sort(function(left, right) {
                const l = left.toLowerCase();
                const r = right.toLowerCase();
                if (l === r) {
                    return left < right ? -1 : 1;
                }
                return l < r ? -1 : 1;
            });

            matches.forEach(function(key) {
                const item = this._createItem(key);
                if (key === this._input.value) {
                    selected = item;
                }
                this._current.push(item);
            }, this);
        }

        if (this._current.length > 0 || (this._search !== null && this._inFocus)) {
            if (this._resizeListener === null) {
                this._suggestionsContainer.style.display = "";
                this._resizeListener = this._onResize.bind(this);
                this._scrollListener = this._onScroll.bind(this);
                win.addEventListener("resize", this._resizeListener, false);
                win.addEventListener("scroll", this._scrollListener, true);
            }

            this._onResize();
        } else {
            if (this._resizeListener !== null) {
                win.removeEventListener("resize", this._resizeListener, false);
                win.removeEventListener("scroll", this._scrollListener, true);
                this._resizeListener = null;
                this._scrollListener = null;
            }
            this._suggestionsContainer.style.display = "none";
        }

        if (selected) {
            selected.element.classList.add("selected");
            selected.element.scrollIntoView(false);
        }
    },

    createMatcher: function(_string) {
        const string = normalize(_string);
        if (string === "") {
            return function(item) {
                return true;
            };
        }
        return function(item) {
            return normalize(item).indexOf(string) !== -1;
        };
    },

    destroy: function() {
        this._data = null;
    },

    element: function() {
        return this._element;
    },

    setData: function(data) {
        this.destroy();

        this._data = data;
        this._update();
    },

    set: function(value) {
        if (this._input.value !== value) {
            this._input.value = value;
            this.trigger("changed");
        }
        this._update();
        this.trigger("commit");
    },

    get: function() {
        return this._input.value || null;
    }
});




// WEBPACK FOOTER //
// ./src/abusesa-js/ui/AutoComplete.js